import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import FormControl from "react-bootstrap/FormControl";
import Spinner from "react-bootstrap/Spinner";
import { AiOutlineClose, AiOutlineWarning } from "react-icons/ai";
import {
  usersAPI,
  ROLES,
  ROLE_LABELS,
  ROLE_HELP,
  USER_STATUS_LABELS,
} from "../../../apis/UsersAPI.jsx";
import "./teamManagement.css";

/* TeamManagement — implements BACKEND_TERMINAL_SPEC_V3 §J. Renders
   the current org's user list plus controls for invite / role-change
   / suspend / remove / transfer-ownership.

   All affordances are gated on the caller's role (from
   /auth/get_permissions). The component is self-contained so it can
   drop into the legacy Settings page as a section without forcing
   the parent class component to grow. */

const fmtDate = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const STATUS_CLS = {
  active:         "tm-pill tm-pill-active",
  pending_invite: "tm-pill tm-pill-pending",
  suspended:      "tm-pill tm-pill-suspended",
  removed:        "tm-pill tm-pill-removed",
};

export default function TeamManagement({ token, currentUserEmail }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("VIEWER");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSent, setInviteSent] = useState(false);

  const [confirmTransfer, setConfirmTransfer] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const loadUsers = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    usersAPI.list(token)
      .then((res) => {
        if (res?.status === 200 && (res.data?.code === 0 || Array.isArray(res.data))) {
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.users || []);
          setUsers(list);
        } else {
          setError("Could not load users.");
          setUsers([]);
        }
      })
      .catch((e) => {
        /* 404 → BE hasn't shipped /users yet. We swallow into an
           empty state rather than a hard error so the rest of the
           page stays usable. */
        if (e?.response?.status === 404) {
          setError("Multi-user is not enabled for this organization yet.");
        } else {
          setError("Could not load users.");
        }
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!token) return;
    usersAPI.getPermissions(token)
      .then((res) => {
        if (res?.status === 200 && res.data) {
          setPermissions(res.data?.data || res.data);
        }
      })
      .catch(() => { /* permission lookup failure → fall back to safe defaults */ });
  }, [token]);

  /* Permission summary. When BE hasn't shipped /auth/get_permissions
     we default to "can do everything" so the OWNER (only user on
     a fresh tenant) is never locked out. The BE will reject any
     unauthorized call regardless, so the FE just hides what it can. */
  const myRole = permissions?.role || "OWNER";
  const canInvite = permissions ? !!permissions.can_invite_users : true;
  const canChangeRoles = permissions ? !!permissions.can_change_roles : true;
  const canRemove = permissions ? !!permissions.can_remove_users : true;
  const canTransfer = permissions ? !!permissions.can_transfer_ownership : (myRole === "OWNER");

  const submitInvite = () => {
    if (!inviteEmail || !inviteName || !inviteRole) {
      setInviteError("Email, name, and role are required.");
      return;
    }
    setInviteBusy(true);
    setInviteError(null);
    usersAPI.invite(token, { email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole })
      .then((res) => {
        if (res?.status === 200 && (res.data?.code === 0 || res.data?.user_id || res.data?.id)) {
          setInviteSent(true);
          setInviteEmail("");
          setInviteName("");
          setInviteRole("VIEWER");
          loadUsers();
          setTimeout(() => {
            setInviteSent(false);
            setShowInvite(false);
          }, 1500);
        } else {
          setInviteError(res?.data?.codeMessage || "Invite failed.");
        }
      })
      .catch((e) => {
        const msg = e?.response?.data?.codeMessage || e?.response?.data?.message || "Invite failed.";
        setInviteError(msg);
      })
      .finally(() => setInviteBusy(false));
  };

  const changeRole = (user, nextRole) => {
    if (!nextRole || nextRole === user.role) return;
    setPendingId(user.user_id || user.id);
    usersAPI.update(token, user.user_id || user.id, { role: nextRole })
      .then(() => loadUsers())
      .catch(() => setError("Role change failed."))
      .finally(() => setPendingId(null));
  };

  const toggleStatus = (user) => {
    const id = user.user_id || user.id;
    const next = user.status === "suspended" ? "active" : "suspended";
    setPendingId(id);
    usersAPI.update(token, id, { status: next })
      .then(() => loadUsers())
      .catch(() => setError("Status change failed."))
      .finally(() => setPendingId(null));
  };

  const doRemove = () => {
    if (!confirmRemove) return;
    const id = confirmRemove.user_id || confirmRemove.id;
    setPendingId(id);
    usersAPI.remove(token, id)
      .then(() => {
        setConfirmRemove(null);
        loadUsers();
      })
      .catch(() => setError("Remove failed."))
      .finally(() => setPendingId(null));
  };

  const doTransfer = () => {
    if (!confirmTransfer) return;
    const id = confirmTransfer.user_id || confirmTransfer.id;
    setPendingId(id);
    usersAPI.transferOwnership(token, id)
      .then(() => {
        setConfirmTransfer(null);
        loadUsers();
      })
      .catch(() => setError("Ownership transfer failed."))
      .finally(() => setPendingId(null));
  };

  const sortedUsers = useMemo(() => {
    /* Owner first, then admins, then by name. Stable enough that the
       row order doesn't dance around when statuses flip. */
    const rank = (r) => ["OWNER","ADMIN","MAKER_CHECKER","MAKER","CHECKER","VIEWER"].indexOf(r);
    return [...users].sort((a, b) => {
      const r = rank(a.role) - rank(b.role);
      if (r !== 0) return r;
      return (a.name || a.email || "").localeCompare(b.name || b.email || "");
    });
  }, [users]);

  return (
    <div className="settings-row-div tm-root">
      <div className="settings-row-div-header tm-header-row">
        <span>Team management</span>
        {canInvite && (
          <Button
            className="small-button outside-primary-button"
            onClick={() => setShowInvite(true)}
          >
            + Invite user
          </Button>
        )}
      </div>

      <div className="tm-description">
        Add maker / checker / viewer roles for your team.
        Sensitive actions can require a separate approver per
        the maker-checker workflow.
      </div>

      {loading ? (
        <div className="tm-loading"><Spinner className="loader-outside" /></div>
      ) : error ? (
        <div className="tm-empty">
          <AiOutlineWarning className="tm-empty-icon" />
          <div>{error}</div>
        </div>
      ) : sortedUsers.length === 0 ? (
        <div className="tm-empty">
          <div>No team members yet. Invite a colleague to get started.</div>
        </div>
      ) : (
        <div className="tm-table">
          <div className="tm-row tm-row-head">
            <div>Name</div>
            <div>Role</div>
            <div>Status</div>
            <div>Last login</div>
            <div className="tm-actions-col" />
          </div>
          {sortedUsers.map((u) => {
            const id = u.user_id || u.id;
            const isMe = currentUserEmail && u.email && u.email.toLowerCase() === currentUserEmail.toLowerCase();
            const isOwner = u.role === "OWNER";
            const busy = pendingId === id;
            return (
              <div key={id || u.email} className={"tm-row" + (busy ? " tm-row-busy" : "")}>
                <div className="tm-name-cell">
                  <div className="tm-name">{u.name || "—"}{isMe && <span className="tm-you">YOU</span>}</div>
                  <div className="tm-email">{u.email}</div>
                </div>
                <div>
                  {canChangeRoles && !isOwner && !isMe ? (
                    <Form.Select
                      value={u.role || "VIEWER"}
                      onChange={(e) => changeRole(u, e.target.value)}
                      disabled={busy}
                      className="tm-role-select"
                    >
                      {ROLES.filter((r) => r !== "OWNER").map((r) => (
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <span className="tm-role-static">{ROLE_LABELS[u.role] || u.role}</span>
                  )}
                  {!isOwner && (
                    <div className="tm-role-help">{ROLE_HELP[u.role] || ""}</div>
                  )}
                </div>
                <div>
                  <span className={STATUS_CLS[u.status] || "tm-pill"}>
                    {USER_STATUS_LABELS[u.status] || u.status || "—"}
                  </span>
                </div>
                <div className="tm-lastlogin">{fmtDate(u.last_login_at)}</div>
                <div className="tm-actions-cell">
                  {!isOwner && !isMe && canChangeRoles && (
                    <button
                      type="button"
                      className="tm-link-btn"
                      onClick={() => toggleStatus(u)}
                      disabled={busy}
                    >
                      {u.status === "suspended" ? "Reactivate" : "Suspend"}
                    </button>
                  )}
                  {!isOwner && !isMe && canRemove && (
                    <button
                      type="button"
                      className="tm-link-btn tm-link-danger"
                      onClick={() => setConfirmRemove(u)}
                      disabled={busy}
                    >
                      Remove
                    </button>
                  )}
                  {canTransfer && !isOwner && !isMe && u.status === "active" && (
                    <button
                      type="button"
                      className="tm-link-btn"
                      onClick={() => setConfirmTransfer(u)}
                      disabled={busy}
                    >
                      Make owner
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Invite modal */}
      <Modal show={showInvite} onHide={() => setShowInvite(false)} centered className="settings-modal">
        <Modal.Header>
          <Modal.Title>Invite user</Modal.Title>
          <AiOutlineClose className="close-button" onClick={() => setShowInvite(false)} />
        </Modal.Header>
        <Modal.Body>
          {inviteSent ? (
            <div className="tm-invite-success">Invite sent.</div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="input-label-outside-div">Email</Form.Label>
                <FormControl
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input-field"
                  type="email"
                  disabled={inviteBusy}
                  placeholder="name@company.com"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="input-label-outside-div">Full name</Form.Label>
                <FormControl
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="input-field"
                  disabled={inviteBusy}
                  placeholder="Jane Doe"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="input-label-outside-div">Role</Form.Label>
                <Form.Select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  disabled={inviteBusy}
                  className="input-field"
                >
                  {ROLES.filter((r) => r !== "OWNER").map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </Form.Select>
                <div className="tm-role-help" style={{ marginTop: 4 }}>{ROLE_HELP[inviteRole]}</div>
              </Form.Group>
              {inviteError && <div className="tm-invite-error">{inviteError}</div>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="small-button outside-secondary-button" onClick={() => setShowInvite(false)} disabled={inviteBusy}>
            Close
          </Button>
          {!inviteSent && (
            <Button
              className="small-button outside-primary-button"
              onClick={submitInvite}
              disabled={inviteBusy}
            >
              {inviteBusy ? <Spinner className="loader-send-outside-div-button" /> : null}
              Send invite
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Remove confirmation */}
      <Modal show={!!confirmRemove} onHide={() => setConfirmRemove(null)} centered className="settings-modal">
        <Modal.Header>
          <Modal.Title>
            <AiOutlineWarning className="warning-icon danger" />
            Remove user
          </Modal.Title>
          <AiOutlineClose className="close-button" onClick={() => setConfirmRemove(null)} />
        </Modal.Header>
        <Modal.Body>
          {confirmRemove && (
            <>
              Remove <b>{confirmRemove.name || confirmRemove.email}</b> from the organization?
              They will lose access immediately. Their audit trail is preserved.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="small-button outside-secondary-button" onClick={() => setConfirmRemove(null)}>
            Cancel
          </Button>
          <Button className="small-button outside-danger-button" onClick={doRemove}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Transfer-ownership confirmation. Per §J.1 there's exactly one
          OWNER per client; transferring makes the current OWNER an
          ADMIN. We warn loudly to make sure that's the intent. */}
      <Modal show={!!confirmTransfer} onHide={() => setConfirmTransfer(null)} centered className="settings-modal">
        <Modal.Header>
          <Modal.Title>
            <AiOutlineWarning className="warning-icon danger" />
            Transfer ownership
          </Modal.Title>
          <AiOutlineClose className="close-button" onClick={() => setConfirmTransfer(null)} />
        </Modal.Header>
        <Modal.Body>
          {confirmTransfer && (
            <>
              Transfer ownership of this organization to <b>{confirmTransfer.name || confirmTransfer.email}</b>?
              You will be demoted to Admin. Only the new Owner can transfer ownership again.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="small-button outside-secondary-button" onClick={() => setConfirmTransfer(null)}>
            Cancel
          </Button>
          <Button className="small-button outside-danger-button" onClick={doTransfer}>
            Transfer ownership
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
